/**
 * Socket Event Handler - Connects socket events to Redux store
 * This should be initialized after the socket is connected
 */
import { getSocket } from './socketService';
import store from 'store';
import {
  addMessage,
  addConversation,
  updateMessageStatus,
  setTypingIndicator,
  setUserOnline,
  setUserOffline,
  incrementUnreadCount,
  markMessagesReadLocally,
  removeMessage,
} from 'store/slices/chatSlice';

let isInitialized = false;

/**
 * Initialize socket event handlers connected to Redux
 */
export const initializeSocketEventHandlers = () => {
  const socket = getSocket();
  
  if (!socket) {
    return;
  }

  if (isInitialized) {
    return;
  }

  isInitialized = true;

  // New message received
  socket.on('new_message', ({ message, conversationId }) => {
    const currentUser = store.getState().auth.userData;
    const currentMessages = store.getState().chat.messages;
    const activeConversationId = store.getState().chat.activeConversationId;
    
    // Prevent duplicate messages
    const exists = currentMessages.some(m => m._id === message._id);
    if (exists) {
      return;
    }
    
    // Add message to store
    store.dispatch(addMessage({ message, conversationId }));
    
    // Check if this is the active conversation (flexible ID matching)
    const isActiveConversation = activeConversationId && (
      conversationId === activeConversationId || 
      conversationId?.endsWith(activeConversationId) || 
      activeConversationId?.endsWith(conversationId?.slice(-8))
    );
    
    // Increment unread if not from current user AND not viewing this conversation
    if (message.sender?._id !== currentUser?._id && !isActiveConversation) {
      store.dispatch(incrementUnreadCount({ conversationId }));
    }
  });

  // Message sent confirmation
  socket.on('message_sent', ({ tempId, message }) => {
    store.dispatch(updateMessageStatus({ tempId, message, status: 'sent' }));
  });

  // Unread count update - this is triggered by the backend, don't duplicate increment
  // We already handle unread in new_message, so just ignore duplicate events
  socket.on('unread_update', ({ conversationId, unreadCount }) => {
    // This event is informational - the unread count is already handled in new_message
    // If needed, we could sync the actual count from the server here
  });

  // New conversation created
  socket.on('new_conversation', (conversation) => {
    store.dispatch(addConversation(conversation));
  });

  // Typing indicator
  socket.on('user_typing', ({ conversationId, user, isTyping }) => {
    store.dispatch(setTypingIndicator({ conversationId, user, isTyping }));
  });

  // Messages read
  socket.on('messages_read', ({ conversationId, readBy, readAt }) => {
    store.dispatch(markMessagesReadLocally({ conversationId, readBy }));
  });

  // Message deleted
  socket.on('message_deleted', ({ messageId, conversationId, deletedForEveryone }) => {
    store.dispatch(removeMessage({ messageId, forEveryone: deletedForEveryone }));
  });

  // User online/offline
  socket.on('user_online', ({ userId }) => {
    store.dispatch(setUserOnline(userId));
  });

  socket.on('user_offline', ({ userId }) => {
    store.dispatch(setUserOffline(userId));
  });

  socket.on('user_status_changed', ({ userId, isOnline }) => {
    if (isOnline) {
      store.dispatch(setUserOnline(userId));
    } else {
      store.dispatch(setUserOffline(userId));
    }
  });

  // Error handling
  socket.on('error', ({ message, tempId }) => {
    console.error('Socket error:', message);
    if (tempId) {
      store.dispatch(updateMessageStatus({ tempId, status: 'failed' }));
    }
  });
};

/**
 * Cleanup socket event handlers
 */
export const cleanupSocketEventHandlers = () => {
  const socket = getSocket();
  
  if (!socket) return;

  socket.off('new_message');
  socket.off('message_sent');
  socket.off('new_conversation');
  socket.off('user_typing');
  socket.off('messages_read');
  socket.off('message_deleted');
  socket.off('user_online');
  socket.off('user_offline');
  socket.off('user_status_changed');
  socket.off('unread_update');
  socket.off('error');

  isInitialized = false;
};

export default {
  initializeSocketEventHandlers,
  cleanupSocketEventHandlers,
};
